import React, { useState } from "react";
import { Box, TextField, Button, Typography, Paper, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/configureStore";
import { setHost, removeHost, setGlobalHost, removeGlobalHost } from "../../slices/apiHostsSlice";
import { ApiRoutes } from "../../constants/ApiRoutes";

export const ApiHostsPage: React.FC = () => {
  const dispatch = useDispatch();
  const hosts = useSelector((state: RootState) => state.apiHosts.hosts);
  const globalHost = useSelector((state: RootState) => state.apiHosts.globalHost);
  const [editingHost, setEditingHost] = useState<{ route: string; host: string } | null>(null);
  const [newHost, setNewHost] = useState("");
  const [editingGlobalHost, setEditingGlobalHost] = useState(false);
  const [newGlobalHost, setNewGlobalHost] = useState("");

  const handleSaveHost = () => {
    if (editingHost && newHost) {
      dispatch(setHost({ route: editingHost.route, host: newHost }));
      setEditingHost(null);
      setNewHost("");
    }
  };

  const handleRemoveHost = (route: string) => {
    dispatch(removeHost(route));
  };

  const handleRemoveAllHosts = () => {
    Object.values(ApiRoutes).forEach((route) => {
      if (hosts[route]) {
        dispatch(removeHost(route));
      }
    });
  };

  const handleEditHost = (route: string, currentHost: string) => {
    setEditingHost({ route, host: currentHost });
    setNewHost(currentHost);
  };

  const handleSaveGlobalHost = () => {
    if (newGlobalHost) {
      dispatch(setGlobalHost(newGlobalHost));
      setEditingGlobalHost(false);
      setNewGlobalHost("");
    }
  };

  const handleEditGlobalHost = () => {
    setEditingGlobalHost(true);
    setNewGlobalHost(globalHost || "");
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" gutterBottom={true}>
            API Hosts Configuration
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemoveAllHosts}
            disabled={Object.keys(hosts).length === 0}
          >
            Remove All Hosts
          </Button>
        </Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom={true}>
            Global Host
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {editingGlobalHost ? (
              <>
                <TextField
                  size="small"
                  value={newGlobalHost}
                  onChange={(e) => setNewGlobalHost(e.target.value)}
                  placeholder="Enter global host URL"
                  sx={{ flex: 1 }}
                />
                <Button variant="contained" onClick={handleSaveGlobalHost}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingGlobalHost(false);
                    setNewGlobalHost("");
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <TextField
                  size="small"
                  value={globalHost || ""}
                  disabled={true}
                  placeholder="No global host set"
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={handleEditGlobalHost}>
                  Edit
                </Button>
                {globalHost && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => dispatch(removeGlobalHost())}
                  >
                    Remove
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Grid container={true} spacing={2}>
            {Object.values(ApiRoutes).map((route) => {
              const currentHost = hosts[route] || "";
              const isEditing = editingHost?.route === route;

              return (
                <Grid item={true} xs={12} key={route}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ minWidth: 300 }}>{route}</Typography>
                    {isEditing ? (
                      <>
                        <TextField
                          size="small"
                          value={newHost}
                          onChange={(e) => setNewHost(e.target.value)}
                          placeholder="Enter host URL"
                          sx={{ flex: 1 }}
                        />
                        <Button variant="contained" onClick={handleSaveHost}>
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditingHost(null);
                            setNewHost("");
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <TextField
                          size="small"
                          value={currentHost}
                          disabled={true}
                          placeholder="No custom host set"
                          sx={{ flex: 1 }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => handleEditHost(route, currentHost)}
                        >
                          Edit
                        </Button>
                        {currentHost && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemoveHost(route)}
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>
    </>
  );
};
